import { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import {
  Alert,
  Avatar,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  TextField,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { formatFullDate, hasConflict, HOURS_PER_DAY } from '../utils'
import { allocateSchedule } from 'src/actions/timesheet.action'
import { successSnackBar, warningSnackBar } from 'src/actions/common.action'
import { getUserWorkHourAssignment, getWorkHourSetting } from 'src/actions/work-hour-settings.action'
import { createAllocationTimes, getAssignmentSetting, getAssignmentSettingId } from '../work-hour-allocation'

function Stat({ value, label }) {
  return (
    <div className="stat-block">
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  )
}
Stat.propTypes = {
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
}

export default function AssignStaffDialog({
  open,
  onClose,
  selection,
  projects,
  roster,
  scheduleMap,
  onConfirm,
}) {
  const dispatch = useDispatch()
  const globalSetting = useSelector((state) => state.globalSetting)
  const [search,     setSearch]     = useState('')
  const [picked,     setPicked]     = useState(() => new Set())
  const [submitting, setSubmitting] = useState(false)
  const [done,       setDone]       = useState(false)

  // Derive selected project-day slots from the selection set
  const slots = useMemo(() => {
    const byProject = new Map()
    selection.forEach((k) => {
      const [projectId, day] = k.split('|')
      const arr = byProject.get(projectId) ?? []
      arr.push(day)
      byProject.set(projectId, arr)
    })
    return [...byProject.entries()].map(([projectId, days]) => ({
      project: projects.find((p) => p.id === projectId),
      days:    days.sort(),
    })).filter((s) => !!s.project)
  }, [selection, projects])

  const allDayKeys = useMemo(() => {
    const set = new Set()
    selection.forEach((k) => set.add(k.split('|')[1]))
    return [...set]
  }, [selection])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return roster.filter(
      (s) => !q || s.name.toLowerCase().includes(q) || s.role.toLowerCase().includes(q),
    )
  }, [search, roster])

  const totals = useMemo(() => {
    const pickedStaff = roster.filter((s) => picked.has(s.id))
    const allocations = pickedStaff.length * selection.size
    const hours = allocations * HOURS_PER_DAY
    // Cost calculated, display hidden in v1
    const cost = pickedStaff.reduce((a, s) => a + (s.hourlyRate || 0) * selection.size * HOURS_PER_DAY, 0)
    let conflicts = 0
    pickedStaff.forEach((s) => {
      allDayKeys.forEach((d) => { if (hasConflict(scheduleMap, s.id, d)) conflicts++ })
    })
    return { allocations, hours, cost, conflicts, people: pickedStaff.length }
  }, [picked, roster, selection, allDayKeys, scheduleMap])

  function togglePick(id) {
    setPicked((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  async function handleConfirm() {
    setSubmitting(true)
    try {
      const pickedStaff = roster.filter((s) => picked.has(s.id))
      const users = (await Promise.all(pickedStaff.map(async (s) => {
        const assignmentResponse = await dispatch(getUserWorkHourAssignment(s.id))
        const assignmentSetting = getAssignmentSetting(assignmentResponse)
        const settingId = getAssignmentSettingId(assignmentResponse)
        const setting = assignmentSetting || (settingId ? await dispatch(getWorkHourSetting(settingId)) : null)
        const resolvedSetting = setting?.data || setting
        if (!resolvedSetting) throw new Error(`${s.name} has no active work-hour setting.`)

        const globalStartTime = globalSetting?.letterhead?.start_time
        const allocations = slots.flatMap(({ project, days }) => {
          if (!project) return []
          return days.map((day) => {
            const times = createAllocationTimes(resolvedSetting, day, { globalStartTime, dailyHours: HOURS_PER_DAY })
            if (!times) throw new Error(`${s.name} has no configured work-hour period for ${day}.`)
            return {
              id:            '',
              projectId:     project.id,
              projectType:   project.type || 'PROJECT',
              stageId:       '',
              taskId:        '',
              taskType:      '',
              taskName:      '',
              ...times,
              allDayWithBreaks: true,
              hourlyRate:    s.hourlyRate || 0,
              previousType:  '',
            }
          })
        })

        return { id: s.id, leaves: [], allocations }
      }))).filter((user) => user.allocations.length > 0)

      if (!users.length) throw new Error('No valid project-day selections were found.')
      await dispatch(allocateSchedule({ users }))
      dispatch(successSnackBar('Staff assigned successfully'))
      setDone(true)
      setTimeout(() => {
        onConfirm()
        setDone(false)
        setPicked(new Set())
        setSearch('')
      }, 800)
    } catch (e) {
      dispatch(warningSnackBar(e?.message || 'Unable to assign staff from the selected work-hour settings.'))
      console.error('[AssignStaff] submit error:', e)
    } finally {
      setSubmitting(false)
    }
  }

  const canSubmit = totals.people > 0 && !submitting && !done

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth className="scheduling-dialog">
      <DialogTitle>
        <Grid container alignItems="center">
          <Grid item xs>
            <span className="dialog-header-title">Assign Staff to Projects</span>
            <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
              {slots.length} {slots.length === 1 ? 'project' : 'projects'} &middot; {selection.size} project-day slots
            </div>
          </Grid>
          <Grid item>
            <IconButton onClick={onClose} aria-label="Close">
              <CloseIcon sx={{ color: '#d0021b' }} />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>

      <DialogContent dividers>
        {done ? (
          <div className="d-flex flex-column align-items-center justify-content-center gap-2 py-5">
            <CheckCircleIcon sx={{ fontSize: 48, color: '#2e9e5b' }} />
            <span style={{ fontSize: 16, fontWeight: 500 }}>Staff assigned</span>
          </div>
        ) : (
          <>
            {/* Selected slots summary */}
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#555' }}>
              Selected slots
            </div>
            <div className="d-flex flex-column gap-2 mt-2 mb-3">
              {slots.map((s) => (
                <div key={s.project.id} className="d-flex flex-wrap align-items-center gap-2">
                  <span style={{ fontSize: 13, fontWeight: 600, minWidth: 200 }}>{s.project.name}</span>
                  <div className="d-flex flex-wrap gap-1">
                    {s.days.map((d) => (
                      <Chip key={d} size="small" variant="outlined" label={formatFullDate(d).replace(/,.*/, '')} />
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <Divider sx={{ my: 2 }} />

            {/* Staff picker */}
            <TextField
              size="small"
              fullWidth
              placeholder="Search staff to assign…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <List
              dense
              sx={{ maxHeight: 240, overflow: 'auto', mt: 1, border: '1px solid #e5e5e5', borderRadius: 1 }}
            >
              {filtered.map((s) => {
                const isPicked     = picked.has(s.id)
                const conflictDays = allDayKeys.filter((d) => hasConflict(scheduleMap, s.id, d)).length
                return (
                  <ListItemButton key={s.id} onClick={() => togglePick(s.id)} dense>
                    <Checkbox edge="start" size="small" checked={isPicked} tabIndex={-1} disableRipple />
                    <ListItemAvatar sx={{ minWidth: 44 }}>
                      <Avatar sx={{ width: 30, height: 30, fontSize: 12, bgcolor: 'var(--brand-color-1)' }}>
                        {s.initials}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={s.name}
                      secondary={s.role || '—'}
                      primaryTypographyProps={{ style: { fontSize: 13, fontWeight: 600 } }}
                      secondaryTypographyProps={{ style: { fontSize: 11 } }}
                    />
                    {conflictDays > 0 && (
                      <Chip
                        size="small"
                        color="warning"
                        variant="outlined"
                        icon={<WarningAmberIcon />}
                        label={`${conflictDays}`}
                      />
                    )}
                  </ListItemButton>
                )
              })}
            </List>

            {totals.conflicts > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {totals.conflicts} of these allocations clash with existing bookings.
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <div className="d-flex align-items-center gap-3 flex-wrap selection-action-bar" style={{ border: 'none', padding: 0 }}>
          <Stat value={`${totals.people}`}       label="Staff"       />
          <Divider orientation="vertical" flexItem />
          <Stat value={`${totals.allocations}`}  label="Allocations" />
          <Divider orientation="vertical" flexItem />
          <Stat value={`${totals.hours}h`}       label="Hours"       />
          {/* Cost stat — hidden in v1, uncomment to show */}
          <div style={{ display: 'none' }}>
            <Divider orientation="vertical" flexItem />
            <Stat value={`$${totals.cost.toLocaleString()}`} label="Est. cost" />
          </div>
        </div>
        <div className="d-flex gap-2">
          <button className="cancel-btn-white" onClick={onClose} type="button">
            Cancel
          </button>
          <button
            className="btnPrimary ms-1"
            disabled={!canSubmit}
            onClick={handleConfirm}
            type="button"
            style={!canSubmit ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
          >
            {submitting ? 'Saving…' : 'Confirm & Assign'}
          </button>
        </div>
      </DialogActions>
    </Dialog>
  )
}

AssignStaffDialog.propTypes = {
  open:        PropTypes.bool.isRequired,
  onClose:     PropTypes.func.isRequired,
  selection:   PropTypes.instanceOf(Set).isRequired,
  projects:    PropTypes.array.isRequired,
  roster:      PropTypes.array.isRequired,
  scheduleMap: PropTypes.instanceOf(Map).isRequired,
  onConfirm:   PropTypes.func.isRequired,
}
